import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "../lib/brand";
import { useQuotePopup } from "../context/QuotePopupContext";

const NAV_LINKS = [
  { id: "products", label: "Products" },
  { id: "journey", label: "Process" },
  { id: "industries", label: "Industries" },
  { id: "bangalore", label: "Bangalore" },
  { id: "enquiry", label: "Enquiry" },
  { id: "contact", label: "Contact" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openPopup } = useQuotePopup();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? "glass-nav" : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="container-luxury flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => scrollTo("hero")}
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <div className="w-10 h-10 flex items-center justify-center border border-[#C9A227] rounded-full">
              <span className="font-serif text-[#C9A227] text-lg leading-none">E</span>
            </div>
            <div className="text-left leading-tight">
              <div className="font-serif text-white text-lg tracking-wide">
                {BRAND.name}
              </div>
              <div className="label-overline text-[#C9A227]/80 text-[0.6rem]">
                Kerala Plantation Luxury
              </div>
            </div>
          </button>

          {/* Desktop Links */}
          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="text-white/85 hover:text-[#C9A227] text-sm tracking-wide transition-colors duration-300 font-light relative group"
              >
                {l.label}
                <span className="absolute left-0 -bottom-1 h-px w-0 bg-[#C9A227] transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="nav-call-btn"
              className="flex items-center gap-2 text-white/80 hover:text-[#C9A227] text-sm transition-colors"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
            <button
              onClick={openPopup}
              data-testid="nav-quote-btn"
              className="btn-gold"
            >
              Get Quote
            </button>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden fixed inset-x-0 top-20 z-30 glass-nav border-t border-white/10"
            data-testid="mobile-nav-drawer"
          >
            <div className="container-luxury py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => {
                    scrollTo(l.id);
                    setOpen(false);
                  }}
                  data-testid={`mobile-nav-${l.id}`}
                  className="text-left text-white/90 hover:text-[#C9A227] text-base font-light tracking-wide"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  openPopup();
                }}
                data-testid="mobile-nav-quote"
                className="btn-gold mt-3 self-start"
              >
                Get Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
